import { useEffect, useRef } from 'react'

import config from '../config'
import { getGAClientId } from '../utils/cookies'

const {
  writeKey: defaultSegmentKey,
  segmentHost,
  fallbackLibraryHost,
} = config.segment

const { writeKey: segmentGen2Key } = config.segmentGen2

/**
 * Initialize & load segment
 */
export const initialiseSegment = (useGen2Key = true) =>
  /* eslint-disable no-unused-expressions */
  (function () {
    var analytics = (window.analytics = window.analytics || [])
    // If the real analytics.js is already on the page return.
    if (analytics.initialize) return
    // If the snippet was invoked already show an error.
    if (analytics.invoked) {
      if (window.console && console.error) {
        console.error('Segment snippet included twice.')
      }
      return
    }
    // Invoked flag, to make sure the snippet
    // is never invoked twice.
    analytics.invoked = true
    // A list of the methods in Analytics.js to stub.
    analytics.methods = [
      'trackSubmit',
      'trackClick',
      'trackLink',
      'trackForm',
      'pageview',
      'identify',
      'reset',
      'group',
      'track',
      'ready',
      'alias',
      'debug',
      'page',
      'once',
      'off',
      'on',
      'addSourceMiddleware',
      'addIntegrationMiddleware',
      'setAnonymousId',
      'addDestinationMiddleware',
    ]
    // Define a factory to create stubs. These are placeholders
    // for methods in Analytics.js so that you never have to wait
    // for it to load to actually record data. The `method` is
    // stored as the first argument, so we can replay the data.
    analytics.factory = function (method) {
      return function () {
        var args = Array.prototype.slice.call(arguments)
        args.unshift(method)
        analytics.push(args)
        return analytics
      }
    }

    // For each of our methods, generate a queueing stub.
    for (var i = 0; i < analytics.methods.length; i++) {
      var key = analytics.methods[i]
      analytics[key] = analytics.factory(key)
    }
    // Define a method to load Analytics.js from our CDN,
    // and that will be sure to only ever load it once.
    analytics.load = function (key, options) {
      // Create an async script element based on your key.
      var script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.onerror = loadFallback.bind(null, options)
      script.src =
        'https://cdn.segment.com/analytics.js/v1/' + key + '/analytics.min.js'
      // Insert our script next to the first script element.
      let first = document.getElementsByTagName('script')[0]
      first.parentNode.insertBefore(script, first)
      analytics._loadOptions = options
    }

    const segmentKey = useGen2Key ? segmentGen2Key : defaultSegmentKey

    function loadFallback(options) {
      var script = document.createElement('script')
      script.type = 'text/javascript'
      script.async = true
      script.src =
        ('https:' === document.location.protocol ? 'https://' : 'http://') +
        fallbackLibraryHost +
        segmentKey +
        '/analytics.min.js'

      // Insert our script next to the first script element.
      let first = document.getElementsByTagName('script')[0]
      first.parentNode.insertBefore(script, first)
      analytics._loadOptions = options
    }
    analytics._writeKey = segmentKey
    // Add a version to keep track of what's in the wild.
    analytics.SNIPPET_VERSION = '4.15.2'
    // Load Analytics.js with your key, which will automatically
    // load the tools you've enabled for your account. Boosh!
    analytics.load(segmentKey, {
      integrations: {
        'Segment.io': { apiHost: segmentHost },
      },
    })
  })()
/* eslint-enable no-unused-expressions */

/**
 * Initial Segment calls
 */
export const SegmentIdentityPage = ({
  eventTitle,
  eventName,
  pageTitle,
  siteTitle,
  subStatus,
  isAuthenticated,
  eventSource,
  identifyOptions = {},
  pageOptions = {},
}) => {
  const hasFired = useRef(false)

  useEffect(() => {
    if (hasFired.current) return
    hasFired.current = true

    const path = window.location.pathname
    const url = window.location.href

    if (isAuthenticated !== undefined) {
      window.analytics.ready(() => {
        const analytics = window.analytics

        // Identify
        const identifyPayload = {
          anonymousId: analytics.user().anonymousId(),
          ...(eventSource && { eventSource }),
          ...identifyOptions,
        }

        analytics.identify(analytics.user().id(), identifyPayload)

        // Page
        const title =
          pageTitle && siteTitle ? `${pageTitle} - ${siteTitle}` : pageTitle

        const pagePayload = {
          path,
          url,
          isAuthenticated,
          ...(eventSource && { eventSource }),
          ...(title && { title }),
          ...pageOptions,
        }

        const pageName = eventTitle || eventName

        if (pageName) {
          analytics.page(pageName, pagePayload)
        } else {
          analytics.page(pagePayload)
        }
      })
    }
  }, [isAuthenticated])

  return null
}

export const SegmentDefault = ({
  eventTitle,
  eventName,
  pageTitle,
  siteTitle,
  subStatus,
  isAuthenticated,
  eventSource,
  identifyOptions = {},
  pageOptions = {},
  userId,
  isAuthResolved,
}) => {
  const anonIdentifiedRef = useRef(false)
  const userIdentifiedRef = useRef(false)
  const pageFiredRef = useRef(false)

  const isReady =
    isAuthResolved === false || (isAuthResolved === true && userId)

  useEffect(() => {
    if (!window.analytics) return
    if (isAuthResolved === null) return
    if (!isReady) return

    window.analytics.ready(() => {
      const analytics = window.analytics

      // Identify
      const identifyPayload = {
        anonymousId: analytics.user().anonymousId(),
        ...(eventSource && { eventSource }),
        ...identifyOptions,
      }

      if (!isAuthResolved && !anonIdentifiedRef.current) {
        analytics.identify(null, identifyPayload)
        anonIdentifiedRef.current = true
      }

      if (isAuthResolved && userId && !userIdentifiedRef.current) {
        analytics.identify(userId, identifyPayload)
        userIdentifiedRef.current = true
      }
    })
  }, [isAuthResolved, userId])

  useEffect(() => {
    if (!window.analytics) return
    if (isAuthResolved === null) return
    if (!isReady) return
    if (pageFiredRef.current) return
    pageFiredRef.current = true

    window.analytics.ready(() => {
      const analytics = window.analytics

      // Page
      const title =
        pageTitle && siteTitle ? `${pageTitle} - ${siteTitle}` : pageTitle

      const pagePayload = {
        path: window.location.pathname,
        url: window.location.href,
        isAuthenticated,
        ...(eventSource && { eventSource }),
        ...(title && { title }),
        ...pageOptions,
      }

      const pageName = eventTitle || eventName

      if (pageName) {
        analytics.page(pageName, pagePayload)
      } else {
        analytics.page(pagePayload)
      }
    })
  }, [isAuthResolved, userId])

  return null
}

export const track = (eventName, ActionDetail, isInActionDetail = false) => {
  const cid = getGAClientId()

  const payload = {
    ...(cid && { cid }),
    ...(isInActionDetail ? { ActionDetail } : ActionDetail),
  }

  window.analytics.track(eventName, payload)
}
